import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { beatBridge } from '../state/beatBridge';
import { gaitBridge } from '../state/gaitBridge';

// BeatHud (?beat=1) — LIVE tuning for the beat-locked gait, no reload. Shows the
// playing track's BPM + a flash on the exact beat grid the feet lock to (via
// beatBridge). The sliders write gaitBridge, which AnimatedResident reads every
// frame — align a foot-plant to the flash by dragging footphase.
export default function BeatHud() {
  const nowPlaying = useWorldStore((s) => s.nowPlaying);
  const modulation = useWorldStore((s) => s.modulation);
  const [phase, setPhase] = useState(0);
  const raf = useRef();

  // Seed controls from URL params (so ?footphase=… still works), then own them.
  const p0 = new URLSearchParams(window.location.search);
  const [footphase, setFootphase] = useState(() => { const v = parseFloat(p0.get('footphase')); return Number.isFinite(v) ? v : 0; });
  const [gaitoct, setGaitoct] = useState(() => { const v = parseFloat(p0.get('gaitoct')); return Number.isFinite(v) && v > 0 ? v : 2; });
  const [bpmOn, setBpmOn] = useState(() => !!parseFloat(p0.get('bpm')));
  const [bpmVal, setBpmVal] = useState(() => { const v = parseFloat(p0.get('bpm')); return Number.isFinite(v) && v > 0 ? v : 100; });

  // Push to the bridge whenever a control changes (and once on mount).
  useEffect(() => { gaitBridge.footphase = footphase; }, [footphase]);
  useEffect(() => { gaitBridge.gaitoct = gaitoct; }, [gaitoct]);
  useEffect(() => { gaitBridge.bpm = bpmOn ? bpmVal : null; }, [bpmOn, bpmVal]);
  useEffect(() => () => { gaitBridge.footphase = null; gaitBridge.gaitoct = null; gaitBridge.bpm = null; }, []);

  useEffect(() => {
    const loop = () => { setPhase(beatBridge.phase); raf.current = requestAnimationFrame(loop); };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  const trackBpm = modulation?.bpm;
  const clockBpm = Math.round(beatBridge.bpm);
  const lit = phase < 0.14;
  const cur = nowPlaying?.current;
  const row = { display: 'flex', justifyContent: 'space-between', gap: 14, margin: '3px 0' };
  const k = { color: '#8a8f9c' };
  const v = { color: '#eafff0', fontVariantNumeric: 'tabular-nums' };
  const ctl = { display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' };
  const lab = { color: '#9dff7d', width: 62, flexShrink: 0 };
  const octBtn = (n) => ({
    flex: 1, cursor: 'pointer', border: '1px solid rgba(157,255,125,0.35)', borderRadius: 6, padding: '3px 0',
    background: gaitoct === n ? 'rgba(157,255,125,0.25)' : 'transparent', color: '#eafff0', font: 'inherit',
  });

  return (
    <div style={{
      position: 'fixed', left: 12, bottom: 12, zIndex: 45, width: 250,
      background: 'rgba(10,12,14,0.92)', border: '1px solid rgba(157,255,125,0.35)',
      borderRadius: 10, padding: '10px 12px', color: '#eafff0',
      fontFamily: 'ui-monospace, monospace', fontSize: 11.5, pointerEvents: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{
          width: 14, height: 14, borderRadius: 7, background: lit ? '#9dff7d' : '#20351f',
          boxShadow: lit ? '0 0 12px #9dff7d' : 'none', transition: 'background .04s',
        }} />
        <span style={{ letterSpacing: 1, color: '#9dff7d' }}>BEAT · ?beat=1</span>
      </div>
      <div style={row}><span style={k}>track BPM</span><span style={v}>{trackBpm ? Math.round(trackBpm) : '— none'}</span></div>
      <div style={row}><span style={k}>beat clock</span><span style={v}>{clockBpm} bpm</span></div>
      <div style={row}><span style={k}>playing</span><span style={v}>{cur?.title ? `${cur.title}`.slice(0, 18) : (nowPlaying?.playing ? 'yes' : 'no')}</span></div>

      <div style={{ height: 1, background: 'rgba(157,255,125,0.18)', margin: '8px 0 4px' }} />

      {/* footphase — align the foot-plant to the flash */}
      <div style={ctl}>
        <span style={lab}>footphase</span>
        <input type="range" min={0} max={1} step={0.01} value={footphase}
          onChange={(e) => setFootphase(parseFloat(e.target.value))} style={{ flex: 1 }} />
        <span style={{ ...v, width: 30, textAlign: 'right' }}>{footphase.toFixed(2)}</span>
      </div>

      {/* gaitoct — 1 = every beat, 2 = half-time, 4 = quarter-time */}
      <div style={ctl}>
        <span style={lab}>gait ÷</span>
        {[1, 2, 4].map((n) => <button key={n} style={octBtn(n)} onClick={() => setGaitoct(n)}>{n}</button>)}
      </div>

      {/* bpm override — test tempo without a track */}
      <div style={ctl}>
        <label style={{ ...lab, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={bpmOn} onChange={(e) => setBpmOn(e.target.checked)} /> bpm
        </label>
        <input type="range" min={60} max={180} step={1} value={bpmVal} disabled={!bpmOn}
          onChange={(e) => setBpmVal(parseFloat(e.target.value))} style={{ flex: 1, opacity: bpmOn ? 1 : 0.4 }} />
        <span style={{ ...v, width: 30, textAlign: 'right' }}>{bpmOn ? bpmVal : '—'}</span>
      </div>

      <div style={{ color: '#6f7580', fontSize: 10, marginTop: 4 }}>drag footphase until the plant hits the flash</div>
    </div>
  );
}
